import React, { useEffect, useState } from "react";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import styled from "styled-components";
import { Button } from "../index";

// Styled scrollable content with custom scrollbar
const ScrollableContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100vh - 200px);
  /* Room for focus rings on inputs — avoid clipping left/right borders */
  padding: 4px 12px 8px 4px;
  margin: 0 -4px;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
  }

  scrollbar-width: thin;
`;

// Styled footer row for actions
const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
  padding: 12px 0 0 0;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const ModalTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 20px;
  font-weight: 700;
  word-break: break-word;
`;

export const CustomModal = ({
  open,
  isOpen,
  title = "",
  children,
  actions = [],
  footer,
  initialState = {},
  onClose = () => {},
  onOpen = () => {},
  onConfirm = () => {},
  loading = false,
  height = "auto",
  width = "400px",
  disabled = false,
  style = {},
  contentStyles = {},
  zIndex,
  showActions = true,
  confirmText = "Confirm",
  savingText = "Loading...",
  // Forwarded to react-responsive-modal. focusTrapped defaults true (unchanged);
  // pass false to stop it auto-focusing the first field on open, which otherwise
  // scrolls a long page to the top.
  focusTrapped = true,
  blockScroll = true,
}) => {
  const [state, setState] = useState(initialState);
  const isShown = Boolean(open ?? isOpen);

  if (!footer && showActions && !actions?.length) {
    actions = [
      {
        label: "Cancel",
        secondary: true,
        onClick: onClose,
      },
      {
        label: loading ? savingText : confirmText,
        primary: true,
        onClick: onConfirm,
        disabled: loading || disabled,
      },
    ];
  }

  useEffect(() => {
    if (isShown) {
      setState(initialState);
      onOpen();
    }
    // eslint-disable-next-line
  }, [isShown]);

  return (
    <Modal
      zIndex={zIndex}
      open={isShown}
      onClose={onClose}
      focusTrapped={focusTrapped}
      blockScroll={blockScroll}
      classNames={{
        modal: `custom-page-layout-modal ${zIndex ? `custom-z-index` : ""}`,
        // Library key is modalContainer (not "container")
        modalContainer: "custom-page-layout-modal-container",
      }}
      center={false}
      styles={{
        modalContainer: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          padding: "16px",
          boxSizing: "border-box",
          overflowX: "hidden",
          overflowY: "auto",
        },
        modal: {
          height,
          // Fill the padded container evenly (equal left/right gap).
          // Avoid 100vw — it ignores scrollbar and overflows the right edge.
          width: "100%",
          maxWidth: width,
          minWidth: 0,
          margin: "auto",
          boxSizing: "border-box",
          borderRadius: "16px",
          padding: "clamp(16px, 4vw, 32px)",
          maxHeight: "calc(100dvh - 32px)",
          overflow: "hidden",
          zIndex: zIndex,
          ...style,
        },
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          justifyContent: "space-between",
          minHeight: "200px",
          maxHeight: "80vh",
          minWidth: 0,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {title && <ModalTitle>{title}</ModalTitle>}
        <ScrollableContent style={contentStyles}>
          <div>{children}</div>
        </ScrollableContent>
        <ModalFooter>
          {footer
            ? footer
            : actions.map((action, idx) => (
                <Button
                  key={idx}
                  onClick={() => action.onClick(state, setState)}
                  disabled={action.disabled || loading}
                  {...action}
                >
                  {action.label}
                </Button>
              ))}
        </ModalFooter>
      </div>
    </Modal>
  );
};
